import { useState, useEffect, useCallback } from 'react';
import { Container, Form, Button, Alert, Spinner, Card, Badge, Table } from 'react-bootstrap';
import Wrapper from '../../components/Wrapper';
import { issueApi } from '../../services/issueApi';
import { uploadImageToS3, getFileExtension } from '../../utils/s3Upload';

const statusVariant = (s) => {
  if (s === 'Resolved') return 'success';
  if (s === 'In Progress') return 'warning';
  return 'secondary';
};

const RaiseIssue = () => {
  const [issue, setIssue] = useState('');
  const [photo, setPhoto] = useState(null);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const [myIssues, setMyIssues] = useState([]);
  const [fetching, setFetching] = useState(true);

  const fetchMyIssues = useCallback(async () => {
    setFetching(true);
    try {
      const res = await issueApi.getMyIssues();
      setMyIssues(Array.isArray(res.data) ? res.data : []);
    } catch {
      setMyIssues([]);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => { fetchMyIssues(); }, [fetchMyIssues]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!issue.trim()) return;
    setLoading(true);
    setSubmitStatus(null);
    
    try {
      let image_url = null;
      if (photo) {
        const ext = getFileExtension(photo);
        image_url = await uploadImageToS3(photo, ext, 'field-officer-issues');
      }

      await issueApi.raiseIssue({ issue, image_url });
      
      setSubmitStatus('success');
      setIssue('');
      setPhoto(null);
      document.getElementById('issue-photo-upload').value = ""; 
      
      fetchMyIssues();
    } catch (err) {
      console.error('Submit Error:', err);
      setSubmitStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Container fluid className="mt-4 px-4">
        <h5 className="mb-4 fw-bold">Raise an Issue</h5>

        {submitStatus === 'success' && (
          <Alert variant="success" onClose={() => setSubmitStatus(null)} dismissible>
            Ticket submitted successfully. The operations team will look into it.
          </Alert>
        )}
        {submitStatus === 'error' && (
          <Alert variant="danger" onClose={() => setSubmitStatus(null)} dismissible>
            Something went wrong. Please try again.
          </Alert>
        )}

        <Card className="border rounded-3 p-3 mb-4" style={{ maxWidth: 600 }}>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Describe your issue</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Explain the problem you are facing..."
                value={issue}
                onChange={(e) => setIssue(e.target.value)}
                required
              />
            </Form.Group>

            {/* Photo Upload Field */}
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">Attach a Photo (Optional)</Form.Label>
              <Form.Control
                id="issue-photo-upload"
                type="file"
                accept="image/*"
                onChange={(e) => setPhoto(e.target.files[0])}
              />
              <Form.Text className="text-muted">
                Upload a screenshot or photo to help us understand the issue better.
              </Form.Text>
            </Form.Group>

            <Button type="submit" variant="dark" disabled={loading || !issue.trim()}>
              {loading ? <Spinner size="sm" animation="border" /> : 'Submit Ticket'}
            </Button>
          </Form>
        </Card>

        <h6 className="fw-bold mb-3">My Submitted Issues</h6>
        {fetching ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : myIssues.length === 0 ? (
          <p className="text-muted">You have not raised any issues yet.</p>
        ) : (
          <Table responsive bordered hover>
            <thead className="table-dark">
              <tr>
                <th>#</th>
                <th>Issue</th>
                <th>Attachment</th>
                <th>Status</th>
                <th>Solution</th>
                <th>Raised At</th>
                <th>Last Updated</th>
              </tr>
            </thead>
            <tbody>
              {myIssues.map((item) => (
                <tr key={item.issue_id}>
                  <td>{item.issue_id}</td>
                  <td>{item.issue}</td>
                  <td>
                    {item.image_url ? (
                      <a href={item.image_url} target="_blank" rel="noopener noreferrer" className="text-primary text-decoration-none">
                        View Photo
                      </a>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    <Badge bg={statusVariant(item.status)}>{item.status}</Badge>
                  </td>
                  <td>{item.solution || '—'}</td>
                  <td>{new Date(item.raised_at).toLocaleDateString()}</td>
                  <td>{new Date(item.updated_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Container>
    </Wrapper>
  );
};

export default RaiseIssue;
